/**
 * BreakpointMarker Component
 *
 * Visual marker indicating a breakpoint is set on a workflow node.
 * Shows as a red dot that can be clicked to toggle the breakpoint.
 */

import { memo, useCallback } from 'react';
import { makeStyles, mergeClasses, tokens, Tooltip } from '@coreai/fluentui-react';
import { useDebugStore, useNodeBreakpoint } from '@/stores/debugStore';

export type BreakpointMarkerProps = {
  /** Node name (action name) to show/toggle breakpoint for */
  nodeName: string;
  /** Position of the marker */
  position?: 'left' | 'top-left';
  /** Whether the marker should be interactive (clickable) */
  interactive?: boolean;
};

const useStyles = makeStyles({
  marker: {
    position: 'absolute',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    cursor: 'pointer',
    transitionProperty: 'transform, opacity',
    transitionDuration: tokens.durationFast,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      transform: 'scale(1.2)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  // Positions
  left: {
    left: '-6px',
    top: '50%',
    transform: 'translateY(-50%)',
    ':hover': {
      transform: 'translateY(-50%) scale(1.2)',
    },
  },
  topLeft: {
    left: '-4px',
    top: '-4px',
  },
  // States
  active: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    border: `2px solid ${tokens.colorPaletteRedBorder1}`,
  },
  disabled: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    opacity: 0.6,
  },
  paused: {
    backgroundColor: tokens.colorPaletteYellowBackground3,
    border: `2px solid ${tokens.colorPaletteYellowBorder1}`,
    // Pulsing animation for paused state
    animationName: {
      from: { boxShadow: `0 0 0 0 ${tokens.colorPaletteYellowBackground3}` },
      to: { boxShadow: `0 0 0 6px transparent` },
    },
    animationDuration: '1s',
    animationIterationCount: 'infinite',
  },
  placeholder: {
    backgroundColor: 'transparent',
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    opacity: 0,
    ':hover': {
      opacity: 1,
    },
  },
  // Inner dot for active breakpoints
  innerDot: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: tokens.colorPaletteRedForeground1,
  },
  innerDotPaused: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
  },
});

/**
 * BreakpointMarker - Visual indicator for node breakpoints
 */
export const BreakpointMarker = memo(({
  nodeName,
  position = 'left',
  interactive = true,
}: BreakpointMarkerProps) => {
  const styles = useStyles();
  const { hasBreakpoint, isEnabled, isPaused } = useNodeBreakpoint(nodeName);
  const toggleBreakpoint = useDebugStore((state) => state.toggleBreakpoint);
  const isDebuggingEnabled = useDebugStore((state) => state.isDebuggingEnabled);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (interactive) {
      toggleBreakpoint(nodeName);
    }
  }, [interactive, nodeName, toggleBreakpoint]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && interactive) {
      e.preventDefault();
      e.stopPropagation();
      toggleBreakpoint(nodeName);
    }
  }, [interactive, nodeName, toggleBreakpoint]);

  // Determine marker state
  const getMarkerClass = () => {
    if (isPaused) return styles.paused;
    if (hasBreakpoint && isEnabled) return styles.active;
    if (hasBreakpoint && !isEnabled) return styles.disabled;
    return styles.placeholder;
  };

  const getPositionClass = () => {
    switch (position) {
      case 'top-left':
        return styles.topLeft;
      case 'left':
      default:
        return styles.left;
    }
  };

  // Build tooltip content
  const getTooltipContent = () => {
    if (isPaused) return 'Paused at breakpoint - click to remove';
    if (hasBreakpoint && isEnabled) return 'Breakpoint enabled - click to remove';
    if (hasBreakpoint && !isEnabled) return 'Breakpoint disabled - click to remove';
    return 'Click to add breakpoint';
  };

  // Only show if debugging is enabled or breakpoint exists
  if (!isDebuggingEnabled && !hasBreakpoint) {
    return null;
  }

  return (
    <Tooltip content={getTooltipContent()} relationship="label" positioning="before">
      <div
        className={mergeClasses(
          styles.marker,
          getPositionClass(),
          getMarkerClass()
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={interactive ? 0 : -1}
        aria-label={`Breakpoint on ${nodeName}: ${hasBreakpoint ? (isEnabled ? 'enabled' : 'disabled') : 'not set'}`}
        aria-pressed={hasBreakpoint}
      >
        {(hasBreakpoint && isEnabled) && (
          <div className={mergeClasses(styles.innerDot, isPaused && styles.innerDotPaused)} />
        )}
      </div>
    </Tooltip>
  );
});

BreakpointMarker.displayName = 'BreakpointMarker';
